import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { postRequest } from "../utils/apiHelper";
import { getToken } from "../utils/appHelper";

export interface SessionData {
  id?: string | number;
  email?: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  title?: string;
  avatar_filename?: string;
  agency_pref?: string | number;
  is_monetise_member?: boolean;
  preference?: any;
  is_client?: number | null;
  user_role?: any;
  ag_owner?: number;
  ag_users?: any[];
  ag_name?: string;
  /** Workspace display currency (ISO 4217), e.g. "USD". */
  currency?: string;
  status?: number | string;
  wsm?: boolean;
  paymentProcessing?: number;
  agency_plan?: any;
  planOptions?: any;
  rText?: any;
  agency_ids?: number[];
  lead_distribution_process?: "pending" | "completed" | "failed" | "default";
  distributed_lead_count?: number;
  created_on?: string;
  countDownInfo?: {
    lead_count: number;
    expires_on: string;
    plan: "default" | "pro" | "levelup";
  }[];
}

interface SessionContextValue {
  session: SessionData | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  isAuthenticated: boolean;
}

const SessionContext = createContext<SessionContextValue | undefined>(
  undefined
);

interface SessionProviderProps {
  children: React.ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const [session, setSession] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSession = useCallback(async () => {
    const token = getToken();

    if (!token) {
      setSession(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [profileResponse, agencyIdsResponse] = await Promise.all([
        postRequest("auth/profile", {}, false, undefined, "default", undefined),
        postRequest(
          "userAgency/getAgencyIds",
          {},
          false,
          undefined,
          "default",
          undefined
        ).catch(() => null), // Don't fail session fetch if agency IDs fail
      ]);

      const sessionData = profileResponse?.data || null;

      if (sessionData && agencyIdsResponse?.data) {
        sessionData.agency_ids = Array.isArray(agencyIdsResponse.data)
          ? agencyIdsResponse.data.map((id: any) => Number(id))
          : [];
      }

      setSession(sessionData);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error("Failed to fetch session");
      setError(error);
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  const value: SessionContextValue = {
    session,
    loading,
    error,
    refetch: fetchSession,
    isAuthenticated: !!session && !!getToken(),
  };

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export const useSession = () => {
  const context = useContext(SessionContext);

  if (context === undefined) {
    throw new Error("useSession must be used within a SessionProvider");
  }

  return context;
};
